import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../context/SubscriptionContext';
import { Clock, AlertTriangle, ChevronRight } from 'lucide-react';

const GracePeriodBanner = ({ hideSubscribeButton = false }) => {
  const navigate = useNavigate();
  const { 
    showGraceBanner, 
    daysRemaining, 
    totalDays, 
    subscriptionTier,
    isGracePeriod 
  } = useSubscription();

  // Don't render if we shouldn't show the banner
  if (!showGraceBanner || !isGracePeriod) return null;

  // Determine urgency styling based on days remaining
  const getUrgencyStyles = () => {
    if (daysRemaining <= 1) {
      return {
        bg: 'bg-gradient-to-r from-red-600 to-red-700',
        border: 'border-red-500',
        icon: <AlertTriangle className="w-5 h-5 text-white" />,
        urgencyText: 'Final Day!'
      };
    } else if (daysRemaining <= 3) {
      return {
        bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
        border: 'border-amber-400',
        icon: <AlertTriangle className="w-5 h-5 text-white" />,
        urgencyText: 'Ending Soon'
      };
    } else if (daysRemaining <= 7) {
      return {
        bg: 'bg-gradient-to-r from-amber-500 to-amber-600',
        border: 'border-amber-400',
        icon: <Clock className="w-5 h-5 text-white" />,
        urgencyText: null
      };
    } else {
      return {
        bg: 'bg-gradient-to-r from-blue-600 to-indigo-600',
        border: 'border-blue-400',
        icon: <Clock className="w-5 h-5 text-white" />,
        urgencyText: null
      };
    }
  };

  const styles = getUrgencyStyles();
  const progressPercentage = ((totalDays - daysRemaining) / totalDays) * 100;

  return (
    <div 
      className={`${styles.bg} ${styles.border} border rounded-xl p-4 mb-6 shadow-lg`}
      data-testid="grace-period-banner"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 p-2 bg-white/20 rounded-lg">
            {styles.icon}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold text-base">
                Trial Period
                {styles.urgencyText && (
                  <span className="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs font-bold animate-pulse">
                    {styles.urgencyText}
                  </span>
                )}
              </h3>
            </div>
            <p className="text-white/90 text-sm mt-0.5">
              <span className="font-bold">{daysRemaining}</span> of {totalDays} days remaining on your{' '}
              <span className="font-semibold">{subscriptionTier}</span> plan trial
            </p>
          </div>
        </div>
        
        {!hideSubscribeButton && (
          <button
            onClick={() => navigate('/admin/plans')}
            className="flex items-center gap-2 px-4 py-2.5 bg-white text-gray-800 rounded-lg font-semibold 
                       hover:bg-gray-100 transition-all shadow-md hover:shadow-lg whitespace-nowrap
                       focus:outline-none focus:ring-2 focus:ring-white/50"
            data-testid="subscribe-now-btn"
          >
            Subscribe Now
            <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Progress bar showing time elapsed */}
      <div className="mt-4">
        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div 
            className="h-full bg-white/80 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        <p className="text-white/70 text-xs mt-1.5 text-right">
          {Math.round(progressPercentage)}% of trial used
        </p>
      </div>
    </div>
  );
};

export default GracePeriodBanner;
